import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./NavBar";
import Sidebar from "./Sidebar";
import { WorkingCompanySelect } from "./WorkingCompany";
import usePermissions from "../../hooks/usePermissions";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { setLastView } from "../../utils/viewPreference";
import "./Layout.css";

const Layout = ({ children }) => {
  // Single source of truth for the sidebar toggle
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Phones: the sidebar is a slide-in menu (hamburger in the top bar)
  const [menuOpen, setMenuOpen] = useState(false);
  const user = useCurrentUser();
  const perms = usePermissions();
  const location = useLocation();
  // Close the phone menu after navigating (render-time adjustment, no effect)
  const [prevPath, setPrevPath] = useState(location.pathname);
  if (location.pathname !== prevPath) {
    setPrevPath(location.pathname);
    if (menuOpen) setMenuOpen(false);
  }
  // HR view shell (route guards already sent users without HR access to My view)
  useEffect(() => {
    if (perms.hasHrAccess && perms.employeeId) setLastView(user.userId, "hr");
  }, [perms.hasHrAccess, perms.employeeId, user.userId]);

  return (
    <div className="layout-root">
      <Navbar onMenu={() => setMenuOpen(true)} />

      <div className="layout-body">
        {menuOpen && (
          <div
            className="layout-backdrop"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
        )}
        {/* Sidebar: 240px expanded, 70px collapsed */}
        {!perms.isLoading && (
          <aside
            className={`layout-aside ${isCollapsed && !menuOpen ? "is-collapsed" : ""} ${menuOpen ? "is-open" : ""}`}
          >
            <Sidebar
              isCollapsed={isCollapsed && !menuOpen}
              setIsCollapsed={(v) =>
                menuOpen ? setMenuOpen(false) : setIsCollapsed(v)
              }
            />
          </aside>
        )}

        <main className="layout-main">
          {/* Phones: the working company (super admin) under the top bar */}
          <div className="layout-company d-md-none">
            <WorkingCompanySelect />
          </div>
          {perms.isLoading ? (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          ) : perms.isError ? (
            <div className="container-fluid py-5 px-lg-4">
              <div className="alert alert-danger">
                {getApiErrorMessage(
                  perms.error,
                  "Could not load your permissions.",
                )}
              </div>
            </div>
          ) : (
            <>
              {location.state?.notLinked && (
                <div
                  className="alert alert-info fs-085 m-3 mb-0 py-2"
                  role="status"
                >
                  <i className="bi bi-info-circle me-2"></i>Your account is not
                  linked to an employee, so My view isn't available.
                </div>
              )}
              <div className="w-100">{children}</div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;
